import type { TextSelection, PDFFile } from '@/types'
import type { SelectionCoords } from '@/interfaces/document-viewer'
import { logger } from '@/utils/logger'

/**
 * Handles text selection functionality for documents
 * Extracted from the monolithic PDFViewer class
 */
export class SelectionManager {
  private container: HTMLElement
  private canvas: HTMLCanvasElement | null = null
  private currentFile: PDFFile | null = null
  private scale: number = 1.2 // Default scale factor
  
  // Selection state
  private selections: TextSelection[] = []
  private isSelecting: boolean = false
  private selectionStart: { x: number; y: number } | null = null
  private currentSelection: HTMLDivElement | null = null
  private selectionOverlay: HTMLDivElement | null = null
  private lastKnownCanvasOffset: { x: number; y: number } = { x: 0, y: 0 }
  private isUpdatingCanvasOffset: boolean = false
  
  // Callbacks
  private onSelectionCreated?: (selection: TextSelection) => void
  private onSelectionUpdated?: (selections: TextSelection[]) => void
  
  constructor(container: HTMLElement) {
    this.container = container
    console.log('🔍 SelectionManager constructor - initial scale:', this.scale)
    this.init()
  }
  
  private init(): void {
    this.canvas = this.container.querySelector('#pdf-canvas') as HTMLCanvasElement
    this.selectionOverlay = this.container.querySelector('#selection-overlay') as HTMLDivElement
    
    if (this.selectionOverlay) {
      this.setupSelectionEventListeners()
    }
    
    // Initialize canvas offset (critical for selections loaded before any drag/zoom)
    // Use setTimeout to ensure DOM layout is complete
    setTimeout(() => {
      this.updateCanvasOffset()
    }, 0)
    
    logger.debug('SelectionManager initialized')
  }
  
  /**
   * Set the current file for context
   */
  setCurrentFile(file: PDFFile | null): void {
    this.currentFile = file
    this.clearSelections()
  }
  
  /**
   * Set callback for when a selection is created
   */
  setOnSelectionCreated(callback: (selection: TextSelection) => void): void {
    this.onSelectionCreated = callback
  }
  
  /**
   * Set callback for when selections are updated
   */
  setOnSelectionUpdated(callback: (selections: TextSelection[]) => void): void {
    this.onSelectionUpdated = callback
  }
  
  /**
   * Create a new selection from coordinates
   */
  createSelection(coords: SelectionCoords): TextSelection {
    if (!this.currentFile) {
      throw new Error('No current file set for selection creation')
    }
    
    const selection: TextSelection = {
      id: this.generateSelectionId(),
      project_id: this.currentFile.project_id || 'unknown',
      document_id: this.currentFile.id,
      document_type: 'pdf', // Default to PDF, will be updated based on file type
      x: coords.x,
      y: coords.y,
      width: coords.width,
      height: coords.height,
      page_number: coords.pageNumber || 1,
      extracted_text: '',
      ocr_confidence: 0,
      thumbnail_data: 'loading'
    }
    
    this.selections.push(selection)
    this.highlightSelection(selection)
    
    // Notify callbacks
    if (this.onSelectionCreated) {
      this.onSelectionCreated(selection)
    }
    
    if (this.onSelectionUpdated) {
      this.onSelectionUpdated(this.selections)
    }
    
    logger.debug('Selection created:', selection.id)
    return selection
  }
  
  /**
   * Get all current selections
   */
  getSelections(): TextSelection[] {
    return [...this.selections]
  }
  
  /**
   * Get selection by ID
   */
  getSelectionById(id: string): TextSelection | undefined {
    return this.selections.find(s => s.id === id)
  }
  
  /**
   * Update a selection
   */
  updateSelection(id: string, updates: Partial<TextSelection>): boolean {
    const index = this.selections.findIndex(s => s.id === id)
    if (index === -1) return false
    
    this.selections[index] = {
      ...this.selections[index],
      ...updates
    }
    
    // Re-highlight the selection
    this.highlightSelection(this.selections[index])
    
    // Notify callbacks
    if (this.onSelectionUpdated) {
      this.onSelectionUpdated(this.selections)
    }
    
    logger.debug('Selection updated:', id)
    return true
  }
  
  /**
   * Remove a selection
   */
  removeSelection(id: string): boolean {
    const index = this.selections.findIndex(s => s.id === id)
    if (index === -1) return false
    
    this.selections.splice(index, 1)
    this.removeSelectionHighlight(id)
    
    // Notify callbacks
    if (this.onSelectionUpdated) {
      this.onSelectionUpdated(this.selections)
    }
    
    logger.debug('Selection removed:', id)
    return true
  }
  
  /**
   * Clear all selections
   */
  clearSelections(): void {
    this.selections.forEach(selection => {
      this.removeSelectionHighlight(selection.id)
    })
    this.selections = []
    
    // Notify callbacks
    if (this.onSelectionUpdated) {
      this.onSelectionUpdated(this.selections)
    }
    
    logger.debug('All selections cleared')
  }
  
  /**
   * Highlight a specific selection
   */
  highlightSelection(selection: TextSelection): void {
    if (!this.selectionOverlay || !this.canvas) return
    
    // Remove existing highlight for this selection
    this.removeSelectionHighlight(selection.id)
    
    // Get current scale from renderer to ensure synchronization
    const currentScale = this.getCurrentScale()
    
    // Use cached canvas offset (updated during setScale or drag)
    const canvasOffsetX = this.lastKnownCanvasOffset.x
    const canvasOffsetY = this.lastKnownCanvasOffset.y
    
    // Convert PDF coordinates to canvas coordinates using current scale
    const canvasX = selection.x * currentScale
    const canvasY = selection.y * currentScale
    
    // Convert canvas coordinates to #pdf-content coordinates (add canvas offset)
    const displayX = canvasX + canvasOffsetX
    const displayY = canvasY + canvasOffsetY
    const displayWidth = selection.width * currentScale
    const displayHeight = selection.height * currentScale
    
    console.log(`🔵 HIGHLIGHT overlay (${selection.id.substring(0, 12)}...):`, {
      pdfCoords: { x: selection.x, y: selection.y, width: selection.width, height: selection.height },
      canvasOffset: { x: canvasOffsetX, y: canvasOffsetY },
      currentScale: currentScale,
      calculation: {
        canvasX: `${selection.x} * ${currentScale} = ${canvasX}`,
        displayX: `${canvasX} + ${canvasOffsetX} = ${displayX}`,
        displayY: `${canvasY} + ${canvasOffsetY} = ${displayY}`
      },
      finalVisualCoords: { left: displayX, top: displayY, width: displayWidth, height: displayHeight }
    })
    
    // Create highlight element with converted coordinates
    const highlight = document.createElement('div')
    highlight.className = 'selection-highlight'
    highlight.dataset.selectionId = selection.id
    highlight.style.position = 'absolute'
    highlight.style.left = `${displayX}px`
    highlight.style.top = `${displayY}px`
    highlight.style.width = `${displayWidth}px`
    highlight.style.height = `${displayHeight}px`
    highlight.style.backgroundColor = 'rgba(59, 130, 246, 0.3)'
    highlight.style.border = '2px solid rgba(59, 130, 246, 0.8)'
    highlight.style.pointerEvents = 'none'
    highlight.style.zIndex = '10'
    
    this.selectionOverlay?.appendChild(highlight)
  }
  
  /**
   * Remove highlight for a specific selection
   */
  removeSelectionHighlight(selectionId: string): void {
    if (!this.selectionOverlay) return
    
    const highlight = this.selectionOverlay.querySelector(`[data-selection-id="${selectionId}"]`)
    if (highlight) {
      highlight.remove()
    }
  }
  
  /**
   * Highlight all selections
   */
  highlightAllSelections(): void {
    this.selections.forEach(selection => {
      this.highlightSelection(selection)
    })
  }
  
  /**
   * Load selections from external source
   */
  loadSelections(selections: TextSelection[]): void {
    this.clearSelections()
    this.selections = [...selections]
    
    // Sync scale with renderer once
    this.getCurrentScale()
    this.updateCanvasOffset()
    
    // Use requestAnimationFrame to ensure renderer is fully initialized
    requestAnimationFrame(() => {
      this.highlightAllSelections()
    })
    
    // Notify callbacks
    if (this.onSelectionUpdated) {
      this.onSelectionUpdated(this.selections)
    }
    
    logger.debug(`Loaded ${selections.length} selections`)
  }
  
  /**
   * Check if currently selecting
   */
  isCurrentlySelecting(): boolean {
    return this.isSelecting
  }
  
  /**
   * Get selection count
   */
  getSelectionCount(): number {
    return this.selections.length
  }
  
  /**
   * Update the cached canvas offset (called when layout changes, with debouncing)
   */
  private updateCanvasOffset(): void {
    if (!this.canvas || this.isUpdatingCanvasOffset) return
    
    this.isUpdatingCanvasOffset = true
    
    // Use requestAnimationFrame to batch multiple offset updates
    requestAnimationFrame(() => {
      const canvasRect = this.canvas!.getBoundingClientRect()
      const pdfContent = this.container.querySelector('#pdf-content') as HTMLElement
      const contentRect = pdfContent?.getBoundingClientRect()
      
      const newOffset = {
        x: contentRect ? canvasRect.left - contentRect.left : 0,
        y: contentRect ? canvasRect.top - contentRect.top : 0
      }
      
      // Only update if offset actually changed
      if (newOffset.x !== this.lastKnownCanvasOffset.x || newOffset.y !== this.lastKnownCanvasOffset.y) {
        this.lastKnownCanvasOffset = newOffset
        // Only log offset changes in development
        if (process.env.NODE_ENV === 'development') {
          console.log('📐 Canvas offset updated:', this.lastKnownCanvasOffset)
        }
      }
      
      this.isUpdatingCanvasOffset = false
    })
  }
  
  /**
   * Set the scale factor (called by PDF renderer, optimized)
   */
  setScale(scale: number): void {
    // Skip update if scale hasn't changed
    if (this.scale === scale) {
      return
    }
    
    console.log('🔍 SelectionManager scale updated:', { oldScale: this.scale, newScale: scale })
    this.scale = scale
    
    // Use requestAnimationFrame to ensure browser completes zoom layout changes
    requestAnimationFrame(() => {
      // Update canvas offset with fresh post-zoom layout values
      this.updateCanvasOffset()
      
      // Redraw all selections with new scale (same as legacy system)
      this.redrawSelections()
    })
  }

  /**
   * Highlight selection by ID
   */
  highlightSelectionById(selectionId: string): void {
    const selection = this.getSelectionById(selectionId)
    if (selection) {
      this.highlightSelection(selection)
    } else {
      logger.warn('Selection not found for highlight:', selectionId)
    }
  }


  /**
   * Redraw selections for current page only (optimized)
   */
  private redrawSelections(): void {
    const currentPage = this.getCurrentPage()
    const currentFileId = this.currentFile?.id
    
    // Filter selections for current page and document only
    const currentPageSelections = this.selections.filter(s => 
      s.page_number === currentPage && 
      s.document_id === currentFileId
    )
    
    // Early exit if no selections for current page
    if (currentPageSelections.length === 0) {
      if (this.selectionOverlay) {
        this.selectionOverlay.innerHTML = ''
      }
      return
    }
    
    // Clear existing visual selections
    if (this.selectionOverlay) {
      this.selectionOverlay.innerHTML = ''
      // Force layout recalculation after clearing
      void this.selectionOverlay.offsetHeight
    }
    
    console.log('🎨 Redrawing selections for current page:', {
      currentPage: currentPage,
      currentFileId: currentFileId,
      totalSelections: this.selections.length,
      currentPageSelections: currentPageSelections.length
    })
    
    // Redraw only current page selections at current scale
    currentPageSelections.forEach((selection, index) => {
      // Convert PDF coordinates back to display coordinates at current scale
      // PDF coordinates are relative to canvas (0,0 = top-left of canvas)
      // Need to convert: PDF → canvas → #pdf-content (accounting for mx-auto centering)
      // CRITICAL: Use cached canvas offset to avoid getBoundingClientRect() instability
      
      if (!this.canvas) return
      
      // Get current scale from renderer to ensure synchronization
      const currentScale = this.getCurrentScale()
      
      // Use cached canvas offset (updated during setScale or drag)
      const canvasOffsetX = this.lastKnownCanvasOffset.x
      const canvasOffsetY = this.lastKnownCanvasOffset.y
      
      // Convert PDF coordinates to canvas coordinates using current scale
      const canvasX = selection.x * currentScale
      const canvasY = selection.y * currentScale
      
      // Convert canvas coordinates to #pdf-content coordinates (add canvas offset)
      const displayX = canvasX + canvasOffsetX
      const displayY = canvasY + canvasOffsetY
      const displayWidth = selection.width * currentScale
      const displayHeight = selection.height * currentScale
      
      console.log(`🔵 PERM overlay #${index + 1} (${selection.id.substring(0, 12)}...):`, {
        pdfCoords: { x: selection.x, y: selection.y, width: selection.width, height: selection.height },
        canvasOffset: { x: canvasOffsetX, y: canvasOffsetY },
        currentScale: currentScale,
        calculation: {
          canvasX: `${selection.x} * ${currentScale} = ${canvasX}`,
          displayX: `${canvasX} + ${canvasOffsetX} = ${displayX}`,
          displayY: `${canvasY} + ${canvasOffsetY} = ${displayY}`
        },
        finalVisualCoords: { left: displayX, top: displayY, width: displayWidth, height: displayHeight }
      })
      
      // Create visual selection rectangle (FLAT structure like temporary selection)
      const selectionContainer = document.createElement('div')
      selectionContainer.className = 'absolute border-2 border-red-500 bg-red-200 bg-opacity-30 pointer-events-none group hover:bg-opacity-40 transition-all'
      selectionContainer.style.left = `${displayX}px`
      selectionContainer.style.top = `${displayY}px`
      selectionContainer.style.width = `${displayWidth}px`
      selectionContainer.style.height = `${displayHeight}px`
      selectionContainer.setAttribute('data-selection-id', selection.id)
      
      // Create selection number badge (direct child, not nested)
      const selectionNumber = document.createElement('div')
      selectionNumber.className = 'absolute -top-6 -left-1 bg-red-500 text-white text-xs font-bold rounded px-2 py-1 shadow-md pointer-events-auto'
      selectionNumber.textContent = `${index + 1}`
      
      selectionContainer.appendChild(selectionNumber)
      
      // Add to selection overlay
      if (this.selectionOverlay) {
        this.selectionOverlay.appendChild(selectionContainer)
      }
    })
  }

  
  /**
   * Get canvas coordinates from mouse event (same as old PDF viewer)
   */
  private getCanvasCoordinates(event: MouseEvent): { x: number; y: number } {
    if (!this.canvas) return { x: 0, y: 0 }
    
    // Get the PDF content container (parent of canvas and overlay)
    const pdfContent = this.canvas.parentElement
    if (!pdfContent) return { x: 0, y: 0 }
    
    // Get the scroll container (pdf-canvas-container)
    const scrollContainer = pdfContent.parentElement
    if (!scrollContainer) return { x: 0, y: 0 }
    
    // Get coordinates relative to the PDF content container
    // This accounts for scroll position automatically since getBoundingClientRect
    // gives us the actual position in the viewport
    const contentRect = pdfContent.getBoundingClientRect()
    const canvasRect = this.canvas.getBoundingClientRect()
    const x = event.clientX - contentRect.left
    const y = event.clientY - contentRect.top
    
    // Debug logging with canvas centering info
    const canvasOffset = {
      x: canvasRect.left - contentRect.left,
      y: canvasRect.top - contentRect.top
    }
    console.log('🔍 Coords:', { 
      x, 
      y, 
      canvasOffset,
      contentWidth: contentRect.width,
      canvasWidth: canvasRect.width,
      isCentered: canvasOffset.x > 0
    })
    
    return { x, y }
  }

  /**
   * Setup event listeners for selection functionality
   */
  private setupSelectionEventListeners(): void {
    if (!this.canvas || !this.selectionOverlay) return
    
    // Mouse down - start selection
    this.canvas!.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return // Only left mouse button
      
      this.isSelecting = true
      
      // Use the same coordinate system as the old PDF viewer
      const coords = this.getCanvasCoordinates(e)
      this.selectionStart = coords
      
      // Create temporary selection element (exact same as old PDF viewer)
      this.currentSelection = document.createElement('div')
      this.currentSelection.className = 'absolute border-2 border-red-500 bg-red-200 bg-opacity-30 pointer-events-none'
      this.currentSelection.style.left = `${this.selectionStart.x}px`
      this.currentSelection.style.top = `${this.selectionStart.y}px`
      this.currentSelection.style.width = '0px'
      this.currentSelection.style.height = '0px'
      
      this.selectionOverlay?.appendChild(this.currentSelection)
      
      e.preventDefault()
    })
    
    // Mouse move - update selection
    this.canvas!.addEventListener('mousemove', (e) => {
      if (!this.isSelecting || !this.selectionStart || !this.currentSelection) return
      
      const coords = this.getCanvasCoordinates(e)
      
      const left = Math.min(this.selectionStart.x, coords.x)
      const top = Math.min(this.selectionStart.y, coords.y)
      const width = Math.abs(coords.x - this.selectionStart.x)
      const height = Math.abs(coords.y - this.selectionStart.y)
      
      // Position selection overlay using original coordinates (same as old PDF viewer)
      this.currentSelection.style.left = `${left}px`
      this.currentSelection.style.top = `${top}px`
      this.currentSelection.style.width = `${width}px`
      this.currentSelection.style.height = `${height}px`
      
      // CRITICAL: Store canvas offset during drag so mouseup uses the same values
      // getBoundingClientRect() can return different values between mousemove and mouseup!
      const canvasRect = this.canvas!.getBoundingClientRect()
      const pdfContent = this.container.querySelector('#pdf-content') as HTMLElement
      const contentRect = pdfContent?.getBoundingClientRect()
      this.lastKnownCanvasOffset.x = contentRect ? canvasRect.left - contentRect.left : 0
      this.lastKnownCanvasOffset.y = contentRect ? canvasRect.top - contentRect.top : 0
      
      console.log('🎯 TEMP overlay (while dragging):', { 
        visualCoords: { left, top, width, height },
        canvasOffset: { x: this.lastKnownCanvasOffset.x, y: this.lastKnownCanvasOffset.y },
        willSaveAs: {
          canvasX: left - this.lastKnownCanvasOffset.x,
          canvasY: top - this.lastKnownCanvasOffset.y,
          pdfX: (left - this.lastKnownCanvasOffset.x) / this.scale,
          pdfY: (top - this.lastKnownCanvasOffset.y) / this.scale
        },
        parent: this.currentSelection.parentElement?.id
      })
    })
    
    // Mouse up - finish selection
    this.canvas!.addEventListener('mouseup', (e) => {
      console.log('🔍 Mouseup event triggered:', { isSelecting: this.isSelecting, hasSelectionStart: !!this.selectionStart, hasCurrentSelection: !!this.currentSelection })
      if (!this.isSelecting || !this.selectionStart || !this.currentSelection) return
      
      const coords = this.getCanvasCoordinates(e)
      
      const left = Math.min(this.selectionStart.x, coords.x)
      const top = Math.min(this.selectionStart.y, coords.y)
      const width = Math.abs(coords.x - this.selectionStart.x)
      const height = Math.abs(coords.y - this.selectionStart.y)
      
      // Only create selection if it's large enough
      if (width > 10 && height > 10) {
        // Convert display coordinates to PDF coordinates
        // CRITICAL: Use the canvas offset stored during mousemove!
        // getBoundingClientRect() can return different values between mousemove and mouseup
        // causing a coordinate mismatch. Use the last known offset from drag.
        
        const canvasOffsetX = this.lastKnownCanvasOffset.x
        const canvasOffsetY = this.lastKnownCanvasOffset.y
        
        // Convert #pdf-content coordinates to canvas coordinates
        const canvasX = left - canvasOffsetX
        const canvasY = top - canvasOffsetY
        
        // Convert canvas coordinates to PDF coordinates
        const pdfX = canvasX / this.scale
        const pdfY = canvasY / this.scale
        const pdfWidth = width / this.scale
        const pdfHeight = height / this.scale
        
        console.log('🎯 Temporary selection coordinates:', {
          originalCoords: { x: coords.x, y: coords.y },
          selectionStart: this.selectionStart,
          visualCoords: { left, top, width, height },
          canvasOffset: { x: canvasOffsetX, y: canvasOffsetY },
          canvasCoords: { x: canvasX, y: canvasY, width, height },
          pdfCoords: { x: pdfX, y: pdfY, width: pdfWidth, height: pdfHeight },
          scale: this.scale,
          calculation: {
            canvasX: `${left} - ${canvasOffsetX} = ${canvasX}`,
            pdfX: `${canvasX} / ${this.scale} = ${pdfX}`,
            note: 'Canvas offset accounts for mx-auto centering'
          }
        })
        
        const selectionCoords: SelectionCoords = {
          x: pdfX,
          y: pdfY,
          width: pdfWidth,
          height: pdfHeight,
          pageNumber: this.getCurrentPage()
        }
        
        this.createSelection(selectionCoords)
        
        // Use requestAnimationFrame to ensure browser layout is complete
        // before updating offset and redrawing
        requestAnimationFrame(() => {
          // Update canvas offset with fresh layout values
          this.updateCanvasOffset()
          
          // Redraw all selections to show the new one (same as legacy system)
          this.redrawSelections()
        })
      }
      
      // Clean up temporary drag overlay
      this.isSelecting = false
      this.selectionStart = null
      if (this.currentSelection) {
        this.currentSelection.remove()
        this.currentSelection = null
      }
    })
    
    // Prevent context menu on canvas
    this.canvas!.addEventListener('contextmenu', (e) => {
      e.preventDefault()
    })
  }
  
  /**
   * Get current page number
   */
  private getCurrentPage(): number {
    // Try to get current page from the PDF renderer
    if ((window as any).pdfViewerRefactored && (window as any).pdfViewerRefactored.ensureRenderer) {
      try {
        return (window as any).pdfViewerRefactored.ensureRenderer().getCurrentPage() || 1
      } catch (error) {
        console.warn('Failed to get current page from renderer:', error)
      }
    }
    
    // Fallback to page 1
    return 1
  }

  /**
   * Get current scale from PDF renderer (with caching for performance)
   */
  private getCurrentScale(): number {
    // Try to get current scale from the PDF renderer
    if ((window as any).pdfViewerRefactored && (window as any).pdfViewerRefactored.ensureRenderer) {
      try {
        const rendererScale = (window as any).pdfViewerRefactored.ensureRenderer().getScale()
        
        if (rendererScale && rendererScale !== this.scale) {
      // Only log scale changes in development
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 SelectionManager syncing scale with renderer:', { 
          oldScale: this.scale, 
          newScale: rendererScale 
        })
      }
          this.scale = rendererScale
        }
        return rendererScale || this.scale
      } catch (error) {
        console.warn('Failed to get current scale from renderer:', error)
      }
    }
    
    // Fallback to current scale
    return this.scale
  }

  /**
   * Generate a unique selection ID
   */
  private generateSelectionId(): string {
    return `selection_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
  
  /**
   * Notify that the page has changed - redraw selections for new page
   */
  onPageChanged(): void {
    console.log('📄 Page changed, redrawing selections for new page')
    console.log('📄 Current scale before redraw:', this.scale)
    this.redrawSelections()
  }

  /**
   * Clear all state
   */
  clearState(): void {
    this.clearSelections()
    this.isSelecting = false
    this.selectionStart = null
    this.currentSelection = null
    this.currentFile = null
  }
}
